import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft } from "lucide-react";
import { TopBar } from "@/components/Brand";
import ProgressBar from "@/components/ProgressBar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Quiz() {
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/quiz/content`).then((r) => { if (alive) setContent(r.data); }).catch(() => setError("Could not load quiz."));
    return () => { alive = false; };
  }, []);

  if (error) return <div className="min-h-screen flex items-center justify-center text-ink/70" data-testid="quiz-error">{error}</div>;
  if (!content) {
    return (
      <div className="min-h-screen bg-cream">
        <TopBar />
        <div className="max-w-3xl mx-auto px-6 py-32 text-center font-display text-3xl text-ink/40 italic-display" data-testid="quiz-loading">
          Loading…
        </div>
      </div>
    );
  }

  const total = content.questions.length;
  const q = content.questions[step];

  const choose = async (letter) => {
    const next = [...answers];
    next[step] = letter;
    setAnswers(next);
    if (step < total - 1) {
      setTimeout(() => setStep(step + 1), 250);
    } else {
      // submit
      setSubmitting(true);
      try {
        const res = await axios.post(`${API}/quiz/submit`, { answers: next });
        sessionStorage.setItem("clarity_result", JSON.stringify(res.data));
        sessionStorage.setItem("clarity_result_id", res.data.id);
        navigate(`/email-gate`);
      } catch (e) {
        setError("Submission failed. Please refresh and try again.");
        setSubmitting(false);
      }
    }
  };

  const back = () => {
    if (step === 0) navigate("/");
    else setStep(step - 1);
  };

  return (
    <div className="min-h-screen bg-cream relative overflow-hidden">
      <svg className="pointer-events-none absolute -bottom-40 -right-40 w-[900px] opacity-[0.07]" viewBox="0 0 600 600" fill="none">
        <path d="M50 300 Q 200 100 350 300 T 650 300" stroke="#0E0E0E" strokeWidth="1" />
        <path d="M0 360 Q 200 160 350 360 T 700 360" stroke="#0E0E0E" strokeWidth="1" />
      </svg>

      <TopBar />

      <main className="px-6 sm:px-10 lg:px-16 pt-4 pb-24">
        <div className="max-w-3xl mx-auto">
          <div className="mb-10 flex items-center justify-between gap-6">
            <button
              type="button"
              onClick={back}
              className="font-sans text-xs tracking-[0.25em] uppercase text-ink/55 inline-flex items-center gap-2 hover:text-sage-700 link-reveal"
              data-testid="quiz-back-button"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              {step === 0 ? "Back to Intro" : "Previous"}
            </button>
            <div className="flex-1 max-w-sm">
              <ProgressBar current={step + 1} total={total} />
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={q.id}
              initial={{ opacity: 0, y: 30, filter: "blur(8px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: -20, filter: "blur(6px)" }}
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            >
              <p className="font-sans text-[10px] sm:text-xs tracking-[0.35em] uppercase text-sage-700 mb-6">
                Question {String(step + 1).padStart(2, "0")} <span className="text-ink/30 ml-2">/ {String(total).padStart(2, "0")}</span>
              </p>

              <h2 className="font-display text-3xl sm:text-5xl leading-[1.05] tracking-tight text-ink mb-12" data-testid={`quiz-question-${q.id}`}>
                {q.prompt}
              </h2>

              <div className="space-y-3">
                {q.options.map((opt, idx) => {
                  const selected = answers[step] === opt.letter;
                  return (
                    <motion.button
                      key={opt.letter}
                      type="button"
                      onClick={() => !submitting && choose(opt.letter)}
                      disabled={submitting}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: 0.08 * idx, ease: "easeOut" }}
                      whileHover={{ x: 6 }}
                      className={`group w-full text-left flex items-start gap-5 p-5 sm:p-6 border rounded-sm transition-colors duration-300 ${
                        selected
                          ? "border-sage-700 bg-sage-50"
                          : "border-cream-300 hover:border-sage-500 bg-cream-100/50"
                      }`}
                      data-testid={`quiz-option-${q.id}-${opt.letter}`}
                    >
                      <span className={`font-display text-3xl leading-none mt-0.5 ${selected ? "text-sage-700" : "text-ink/30 group-hover:text-sage-500"} transition-colors`}>
                        {opt.letter}
                      </span>
                      <span className="font-sans text-base sm:text-lg text-ink/85 leading-snug">
                        {opt.text}
                      </span>
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>
          </AnimatePresence>

          {submitting && (
            <div className="mt-12 text-center font-display italic-display text-2xl text-sage-700 animate-pulse" data-testid="quiz-submitting">
              Reading your nervous system…
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
